// config/importConfigs/relationsImportConfig.js

import { getDomainConfig } from "../domainConfig";
import { normalizePhone, isValidMobile } from "../../utils/phoneNormalizer";

const getNLConfig = (financialLabels, defaultCountry) => ({
  templateColumns: [
    {
      label: "bulkImport.columns.name",
      fieldKey: "Name",
      required: true,
      type: "string",
      maxLength: 255,
      example: "Example B.V.",
      description: "Company name",
    },
    {
      label: "bulkImport.columns.type",
      fieldKey: "Type",
      required: true,
      type: "string",
      example: "Prospect",
      description: "Prospect or Client",
      dropdownOptions: ["Prospect", "Client"],
    },
    {
      label: "bulkImport.columns.status",
      fieldKey: "Status",
      required: true,
      type: "string",
      example: "Active",
      description: "Active or Inactive",
      dropdownOptions: ["Active", "Inactive"],
    },
    {
      label: "bulkImport.columns.contactperson",
      fieldKey: "Contactperson",
      required: true,
      type: "string",
      maxLength: 255,
      example: "John Doe",
      description: "Contact person name",
    },
    {
      label: "bulkImport.columns.streetName",
      fieldKey: "StreetName",
      required: true,
      type: "string",
      maxLength: 255,
      example: "Business Park",
      description: "Street name",
    },
    {
      label: "bulkImport.columns.postCode",
      fieldKey: "PostCode",
      required: true,
      type: "string",
      example: "1234AB",
      description: "Format: 1234AB",
    },
    {
      label: "bulkImport.columns.city",
      fieldKey: "City",
      required: true,
      type: "string",
      maxLength: 100,
      example: "Amsterdam",
      description: "City",
    },
    {
      label: "bulkImport.columns.country",
      fieldKey: "Country",
      required: true,
      type: "string",
      maxLength: 100,
      example: "Netherlands",
      description: "Country",
    },
    {
      label: "bulkImport.columns.email1",
      fieldKey: "Emailaddress1", 
      required: true,
      type: "email",
      example: "contact@example.com",
      description: "Primary email",
    },
    {
      label: "bulkImport.columns.invoiceEmail",
      fieldKey: "InvoiceEmail",
      required: true,
      type: "email",
      example: "invoices@example.com",
      description: "Invoice email",
    },
    {
      label: "bulkImport.columns.email2", 
      fieldKey: "Emailaddress2",
      required: false,
      type: "email",
      example: "info@example.com",
      description: "Secondary email (optional)",
    },
    {
      label: "bulkImport.columns.phone",
      fieldKey: "PhoneNumber",
      required: false,
      type: "string",
      example: "0201234567",
      description: "Phone (optional)",
    },
    {
      label: "bulkImport.columns.mobile",
      fieldKey: "MobileNumber", 
      required: false,
      type: "string",
      example: "0612345678",
      description: "Mobile (optional)",
    },
    {
      label: "bulkImport.columns.houseNumber",
      fieldKey: "HouseNumber",
      required: false,
      type: "string",
      example: "42",
      description: "House number (optional)",
    },
    {
      label: "bulkImport.columns.houseAddition",
      fieldKey: "HouseAddition",
      required: false,
      type: "string",
      example: "A",
      description: "Addition (optional)",
    },
    {
      label: "bulkImport.columns.cocNumber",
      fieldKey: "CocNumber",
      required: false,
      type: "string",
      example: financialLabels.cocNumberPlaceholder,
      description: "CoC number (optional)",
    },
    {
      label: "bulkImport.columns.vatNumber",
      fieldKey: "VatNumber",
      required: false,
      type: "string",
      example: financialLabels.vatPlaceholder,
      description: "VAT number (optional)",
    },
    {
      label: "bulkImport.columns.iban",
      fieldKey: "Iban",
      required: false,
      type: "string",
      example: "NL91ABNA0417164300",
      description: "IBAN (optional)",
    },
  ],

  validationRules: {
    Name: { required: true, label: "Name" },
    Type: {
      required: true,
      label: "Type",
      validate: (v) => ["prospect", "client"].includes(String(v).trim().toLowerCase()),
      message: "bulkImport.errors.invalidType",
      transform: (v) => String(v).trim().toLowerCase() === "prospect" ? 0 : 1,
    },
    Status: {
      required: true,
      label: "Status",
      validate: (v) => ["active", "inactive"].includes(String(v).trim().toLowerCase()),
      message: "bulkImport.errors.invalidStatus",
      transform: (v) => String(v).trim().toLowerCase() === "active" ? 0 : 1,
    },
    Contactperson: { required: true, label: "Contact person" },
    StreetName: { required: true, label: "Street name" },
    PostCode: {
      required: true,
      label: "Postcode",
      pattern: /^[0-9]{4}\s?[A-Za-z]{2}$/,
       message: "bulkImport.errors.invalidPostcodeNL",
    },
    City: { required: true, label: "City" },
    Country: { 
      required: true, 
      label: "Country", 
      transform: (value) => String(value).trim(), 
    },
    Emailaddress1: {
      required: true,
      label: "Email",
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "bulkImport.errors.invalidEmail",
    },
    InvoiceEmail: {
      required: true,
      label: "Invoice email",
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "bulkImport.errors.invalidInvoiceEmail",
    },
    Emailaddress2: {
      required: false,
      label: "Email 2",
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "bulkImport.errors.invalidSecondaryEmail",
    },
    Iban: {
      required: false,
      label: "IBAN",
      pattern: /^NL\d{2}[A-Z]{4}\d{10}$/i,
       message: "bulkImport.errors.invalidIban",
    },
    PhoneNumber: {
      required: false,
      label: "Phone",
      transform: (value) => normalizePhone(value, defaultCountry),
    },
    MobileNumber: {
      required: false,
      label: "Mobile",
      validate: (value) => isValidMobile(value, defaultCountry),
      message: "bulkImport.errors.invalidMobile",
      transform: (value) => normalizePhone(value, defaultCountry),
    },
  },
});

const getINConfig = (financialLabels, defaultCountry) => ({
  templateColumns: [
    {
      label: "bulkImport.columns.name", 
      fieldKey: "Name",  
      required: true,
      type: "string",
      maxLength: 255,
      example: "Sample Company",
      description: "Company name",
    },
    {
      label: "bulkImport.columns.type",
      fieldKey: "Type",
      required: true,
      type: "string",
      example: "Prospect",
      description: "Prospect or Client",
      dropdownOptions: ["Prospect", "Client"],
    },
    {
      label: "bulkImport.columns.status",
      fieldKey: "Status",
      required: true,
      type: "string",
      example: "Active",
      description: "Active or Inactive",
      dropdownOptions: ["Active", "Inactive"],
    },
    {
      label: "bulkImport.columns.contactperson",  
      fieldKey: "Contactperson",
      required: true,
      type: "string",
      maxLength: 255,
      example: "Rahul Sharma",
      description: "Contact person name",
    },
    {
      label: "bulkImport.columns.streetName",  
      fieldKey: "StreetName",
      required: true,
      type: "string",
      maxLength: 255,
      example: "MG Road",
      description: "Street name",
    },
    {
      label: "bulkImport.columns.postCode",  
      fieldKey: "PostCode",
      required: true,
      type: "string",
      example: "400001",
      description: "Format: 400001",
    },
    {
      label: "bulkImport.columns.city",
      fieldKey: "City",  
      required: true,
      type: "string",
      maxLength: 100,
      example: "Mumbai",
      description: "City",
    },
    {
      label: "bulkImport.columns.country",
      fieldKey: "Country",  
      required: true,
      type: "string",
      maxLength: 100,
      example: "India",
      description: "Country",
    },
    {
      label: "bulkImport.columns.email1",
      fieldKey: "Emailaddress1",  
      required: true,
      type: "email",
      example: "contact@example.com",
      description: "Primary email",
    },
    {
      label: "bulkImport.columns.invoiceEmail",
      fieldKey: "InvoiceEmail",  
      required: true,
      type: "email",
      example: "invoices@example.com",
      description: "Invoice email",
    },
    {
      label: "bulkImport.columns.email2",
      fieldKey: "Emailaddress2",  
      required: false,
      type: "email",
      example: "info@example.com",
      description: "Secondary email (optional)",
    },
    {
      label: "bulkImport.columns.phone",
      fieldKey: "PhoneNumber",  
      required: false,
      type: "string",
      example: "02226543210",
      description: "Phone (optional)",
    },
    {
      label: "bulkImport.columns.mobile",
      fieldKey: "MobileNumber",  
      required: false,
      type: "string",
      example: "9876543210",
      description: "Mobile (optional)",
    },
    {
      label: "bulkImport.columns.houseNumber",
      fieldKey: "HouseNumber",  
      required: false,
      type: "string",
      example: "42",
      description: "House number (optional)",
    },
    {
      label: "bulkImport.columns.houseAddition",
      fieldKey: "HouseAddition", 
      required: false,
      type: "string",
      example: "A",
      description: "Addition (optional)",
    },
    {
      label: "bulkImport.columns.gstNumber",  
      fieldKey: "GstNumber",
      required: false,
      type: "string",
      example: financialLabels.gstPlaceholder,
      description: "GST number (optional)",
    },
    {
      label: "bulkImport.columns.accountNumber",
      fieldKey: "AccountNumber", 
      required: false,
      type: "string",
      example: financialLabels.accountPlaceholder,
      description: "Account number (optional)",
    },
    {
      label: "bulkImport.columns.ifscCode",
      fieldKey: "IfscCode",  
      required: false,
      type: "string",
      example: financialLabels.ifscPlaceholder,
      description: "IFSC code (optional)",
    },
  ],

  validationRules: {
    Name: { required: true, label: "Name" },
    Type: {
      required: true,
      label: "Type",
      validate: (v) => ["prospect", "client"].includes(String(v).trim().toLowerCase()),
      message: "bulkImport.errors.invalidType",
      transform: (v) => String(v).trim().toLowerCase() === "prospect" ? 0 : 1,
    },
    Status: {
      required: true,
      label: "Status",
      validate: (v) => ["active", "inactive"].includes(String(v).trim().toLowerCase()),
      message: "bulkImport.errors.invalidStatus",
      transform: (v) => String(v).trim().toLowerCase() === "active" ? 0 : 1,
    },
    Contactperson: { required: true, label: "Contact person" },
    StreetName: { required: true, label: "Street name" },
    PostCode: {
      required: true,
      label: "Postcode",
      pattern: /^[0-9]{6}$/,
      message: "bulkImport.errors.invalidPostcodeIN",
    },
    City: { required: true, label: "City" },
    Country: {
      required: true,
      label: "Country",
      transform: (value) => String(value).trim(),
    },
    Emailaddress1: {
      required: true,
      label: "Email",
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "bulkImport.errors.invalidEmail",
    },
    InvoiceEmail: {
      required: true,
      label: "Invoice email",
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "bulkImport.errors.invalidInvoiceEmail",
    },
    Emailaddress2: {
      required: false,
      label: "Email 2",
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "bulkImport.errors.invalidSecondaryEmail",
    },
    GstNumber: {
      required: false,
      label: "GST Number",
      pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
      message: "bulkImport.errors.invalidGst",
      messageParams: { example: financialLabels.gstPlaceholder },
    },
    AccountNumber: { 
      required: false,
      label: "Account Number",
    },
    IfscCode: {
      required: false,
      label: "IFSC Code",
      pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/,
      message: "bulkImport.errors.invalidIfsc",
      messageParams: { example: financialLabels.ifscPlaceholder },
    },
    PhoneNumber: {
      required: false,
      label: "Phone",
      transform: (v) => normalizePhone(v, defaultCountry),
    },
    MobileNumber: {
      required: false,
      label: "Mobile",
      validate: (v) => isValidMobile(v, defaultCountry),
      message: "bulkImport.errors.invalidMobile",
      transform: (v) => normalizePhone(v, defaultCountry),
    },
  },
});

export const relationsImportConfig = () => {
  const domainConfig = getDomainConfig();
  const { financialLabels } = domainConfig;
  const isIN = financialLabels.currencyCode === "INR";
  const defaultCountry = (domainConfig.defaultPhoneCountry || "in").toUpperCase();

  const domainSpecific = isIN
    ? getINConfig(financialLabels, defaultCountry)
    : getNLConfig(financialLabels, defaultCountry);

  return {
    moduleName: "relations",
    buttonKey: "bulkImport.relationImportButton",
    titleKey: "bulkImport.relationTitle",
    api: {
      fetchExisting: `${process.env.REACT_APP_API_URL}/relationRoute/relations`,
      import: `${process.env.REACT_APP_API_URL}/relationRoute/bulkImport`,
    },
    duplicateCheck: {
      enabled: true,
      fields: ["Name", "Emailaddress1"],
      message: "Relation with same name and email already exists",
      inFileMessage: "bulkImport.errors.duplicateInFile",
      checkDuplicate: (row, existingRecords) => {
        const match = existingRecords.find(
          (r) =>
            r.Name?.toLowerCase() === row.Name?.toLowerCase() &&
            r.Emailaddress1?.toLowerCase() === row.Emailaddress1?.toLowerCase(),
        );
        return match
          ? { reason: "Relation with same name and email already exists" }
          : null;
      },
    },
    ...domainSpecific,
  };
};